import React, { useState, useMemo } from 'react';
import './TurnoverChart.css';
import turnoverData from '../../data/turnover';

function formatINR(num) {
  return num.toLocaleString('en-IN', { maximumFractionDigits: 0 });
}

function getShortLabel(year) {
  const parts = year.split('-');
  if (parts.length === 2) return parts[0].slice(-2) + '-' + parts[1].slice(-2);
  return year;
}

function TurnoverChart() {
  const data = turnoverData;
  const max = useMemo(() => Math.max(...data.map(d => d.value)), [data]);

  const width = 1100;
  const height = 360;
  const padding = { left: 92, right: 40, top: 28, bottom: 70 };
  const innerW = width - padding.left - padding.right;
  const innerH = height - padding.top - padding.bottom;

  const barGap = 18; // gap between bars
  const barCount = data.length;
  const totalGap = barGap * (barCount - 1);
  const barWidth = (innerW - totalGap) / barCount;

  const bars = data.map((d, i) => {
    const x = padding.left + i * (barWidth + barGap);
    const h = (d.value / max) * innerH;
    const y = padding.top + (innerH - h);
    return { ...d, x, y, h, w: barWidth };
  });

  // progress line points: use center-x of each bar and top of bar
  const progressPoints = bars.map(b => ({ x: b.x + b.w / 2, y: b.y }));

  // build path and extend it slightly beyond the last point so the arrow marker appears 'forward'
  let progressPath = '';
  if (progressPoints.length) {
    progressPath = progressPoints.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');

    if (progressPoints.length >= 2) {
      const last = progressPoints[progressPoints.length - 1];
      const prev = progressPoints[progressPoints.length - 2];
      const dx = last.x - prev.x;
      const dy = last.y - prev.y;
      const len = Math.sqrt(dx * dx + dy * dy) || 1;
      const forward = Math.max(bars[0].w * 0.25, 18); // extend by a quarter bar width or 18px
      const nx = last.x + (dx / len) * forward;
      const ny = last.y + (dy / len) * forward;
      progressPath += ` L ${nx} ${ny}`;
    } else {
      // single point - nudge slightly to the right
      progressPath += ` L ${progressPoints[0].x + 24} ${progressPoints[0].y}`;
    }
  }

  const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, label: '', value: '' });

  function showTip(b) {
    setTooltip({ visible: true, x: b.x + b.w / 2, y: b.y - 10, label: b.year + (b.expected ? ' (expected)' : ''), value: formatINR(b.value) });
  }
  function hideTip() { setTooltip(t => ({ ...t, visible: false })); }

  return (
    <section className="turnover-section">
      <div className="turnover-container">
        <div className="turnover-header">
          <h2 className="turnover-title">Turnover</h2>
          <p className="turnover-sub">Financial year turnover (INR)</p>
        </div>

        <div className="turnover-chart-wrap">
          <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="xMidYMid meet" className="turnover-svg">
            <defs>
              <linearGradient id="barGrad" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor="#7ee29b" stopOpacity="1" />
                <stop offset="100%" stopColor="#3ab86b" stopOpacity="1" />
              </linearGradient>

              <marker id="arrow" viewBox="0 0 10 10" refX="10" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                <path d="M 0 0 L 10 5 L 0 10 z" fill="#2d7a4a" />
              </marker>
            </defs>

            {/* grid lines */}
            <g className="grid-lines">
              {[0, 0.25, 0.5, 0.75, 1].map((t, i) => (
                <line key={i} x1={padding.left} x2={padding.left + innerW} y1={padding.top + t * innerH} y2={padding.top + t * innerH} stroke="#f1f6f4" strokeWidth="1" />
              ))}
            </g>

            {/* bars */}
            <g className="bars">
              {bars.map((b, i) => (
                <g key={i}>
                  <rect x={b.x} y={b.y} width={b.w} height={b.h} rx="6" fill="url(#barGrad)" stroke={b.expected ? '#2d7a4a' : 'rgba(0,0,0,0.06)'} strokeWidth={b.expected ? 1.6 : 0.6} onMouseEnter={() => showTip(b)} onMouseLeave={hideTip} />
                  {/* small label under bar */}
                  <text x={b.x + b.w / 2} y={height - 36} fontSize="12" fill="#6b7d8a" textAnchor="middle">{getShortLabel(b.year)}</text>
                </g>
              ))}
            </g>

            {/* progress line with arrow */}
            <path d={progressPath} fill="none" stroke="#2d7a4a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" markerEnd="url(#arrow)" className="progress-line" />

            {/* y-axis labels (rounded ticks) */}
            <g className="y-labels">
              {(() => {
                const steps = 4;
                const rawStep = Math.ceil(max / steps);
                // round step to nearest 100000 for cleaner labels
                const roundBase = 100000;
                const tickStep = Math.ceil(rawStep / roundBase) * roundBase;
                const ticks = [];
                const top = tickStep * steps;
                for (let i = 0; i <= steps; i++) {
                  ticks.push(top - i * tickStep);
                }
                return ticks.map((val, i) => {
                  const t = i / steps;
                  const y = padding.top + t * innerH + 6;
                  // display in crores for readability (1 Cr = 1,00,00,000)
                  const inCrore = (val / 10000000);
                  const label = `${inCrore % 1 === 0 ? inCrore.toFixed(0) : inCrore.toFixed(2)} Cr`;
                  return <text key={i} x={padding.left - 18} y={y} fontSize="12" fill="#9aa7ad" textAnchor="end">₹ {label}</text>
                })
              })()}
            </g>
          </svg>

          {tooltip.visible && (
            <div className="turnover-tooltip" style={{ left: tooltip.x, top: tooltip.y }}>
              <div className="tooltip-year">{tooltip.label}</div>
              <div className="tooltip-value">₹ {tooltip.value}</div>
            </div>
          )}
        </div>

        <div className="turnover-legend">
          <div className="legend-item"><span className="legend-swatch" /> Turnover (INR)</div>
        </div>
      </div>
    </section>
  );
}

export default TurnoverChart;
